
import { ObjectFactory } from './factory';

/*
    model.ts
    Perform all imports of defined objects
*/

/*
    Re-export the schema type tokens first, before any object module is required.

    The tokens live in a dependency-free leaf module (`./types`) so they are
    fully initialised the moment this module starts evaluating. Object modules read
    these tokens (e.g. `PlaceType`) from `../types` while building their schemas; if
    the tokens were declared further down in this file, a CommonJS load triggered
    mid-graph would hit them in the temporal dead zone. Re-exporting up here keeps
    them available regardless of evaluation order.
*/
export * from './types';

import { Address } from './objects/address';
import { AskWaiter } from './objects/askwaiter';
import { AccountInvoice } from './objects/accountinvoice';
import { Audit } from './objects/audit';
import { TillChange } from './objects/tillchange';
import { CodescanDevice } from './objects/codescan';
import { Pinpad } from './objects/pinpad';
import { Category } from './objects/category';
import { CategoryDep } from './objects/categorydep';
import { Client } from './objects/client';
import { DeviceOption } from './objects/deviceopt';
import { Discount } from './objects/discount';
import { DiscountPeriod } from './objects/discountperiod';
import { DiscountProduct } from './objects/discountproduct';
import { DrawItem } from './objects/draw';
import { Employee } from './objects/employee';
import { Extra } from './objects/extra';
import { ExtraPeriod } from './objects/extraperiod';
import { ExtraOption } from './objects/extraoption';
import { ExtraProduct } from './objects/extraproduct';
import { ExtraTable } from './objects/extratable';
import { Family } from './objects/family';
import { FloorArea } from './objects/floorarea';
import { FamilyPeriod } from './objects/familyperiod';
import { FamilyProduct } from './objects/familyproduct';
import { I18nValue } from './objects/i18nvalue';
import { MixedPayment } from './objects/mixedpay';
import { PinpadPay } from './objects/pinpadpay';
import { Offer } from './objects/offer';
import { OfferPeriod } from './objects/offerperiod';
import { OfferOption } from './objects/offeroption';
import { OfferProduct } from './objects/offerproduct';
import { PayAccount } from './objects/payaccount';
import { Payment } from './objects/payment';
import { Place } from './objects/place';
import { Stripe } from './objects/stripe';
import { PlaceLink } from './objects/placelink';
import { PlaceOption } from './objects/placeopt';
import { PosDevice } from './objects/device';
import { PriceZone } from './objects/pricezone';
import { PriceTime } from './objects/pricetime';
import { ProductPrice } from './objects/productprice';
import { OptionPrice } from './objects/optionprice';
import { Preselect } from './objects/preselect';
import { PreselectOpt } from './objects/preselectopt';
import { PrintDevice } from './objects/printer';
import { PrinterProduct } from './objects/printerproduct';
import { PrinterProductOpt } from './objects/printerproductopt';
import { Product }  from './objects/product';
import { ProductOpt } from './objects/productopt';
import { QrCode } from './objects/qrcode';
import { QrScan } from './objects/qrscan';
import { Swinvoice } from './objects/swinvoice';
import { Rasppi } from './objects/rasppi';
import { RasppiConnect } from './objects/rasppiconnect';
import { ScaleDevice } from './objects/scale';
import { Session } from './objects/session';
import { FCM } from './objects/fcm';
import { Ticket } from './objects/ticket';
import { Till } from './objects/till';
import { TicketChange } from './objects/ticketchange';
import { TicketData } from './objects/ticketdata';
import { TicketDiscount } from './objects/ticketdiscount';
import { TicketInvoice } from './objects/ticketinvoice';
import { TicketProduct } from './objects/ticketproduct';
import { TicketExtra } from './objects/ticketextra';
import { TicketOffer } from './objects/ticketoffer';
import { TicketOption } from './objects/ticketoption';
import { Ticketbai } from './objects/ticketbai';
import { Ticketsii } from './objects/ticketsii';
import { Verifactu } from './objects/verifactu';
import { User } from './objects/user';

/*
    Performs all the object registration in the factory
*/

export function ObjectRegistry(){
    ObjectFactory.register('ACCOUNTINVOICE', AccountInvoice);
    ObjectFactory.register('AUDIT', Audit);
    ObjectFactory.register('TILLCHANGE', TillChange);
    ObjectFactory.register('ADDRESS', Address);
    ObjectFactory.register('ASKWAITER', AskWaiter);
    ObjectFactory.register('CODESCAN', CodescanDevice);
    ObjectFactory.register('PINPAD', Pinpad);
    ObjectFactory.register('CATEGORY', Category);
    ObjectFactory.register('CATEGORYDEP', CategoryDep);
    ObjectFactory.register('INVOICEBUSINESS', Client);
    ObjectFactory.register('DEVICEOPT', DeviceOption);
    ObjectFactory.register('DISCOUNT', Discount);
    ObjectFactory.register('DISCOUNTPERIOD', DiscountPeriod);
    ObjectFactory.register('DISCOUNTPRODUCT', DiscountProduct);
    ObjectFactory.register('DRAWITEM', DrawItem);
    ObjectFactory.register('STAFF', Employee);
    ObjectFactory.register('EXTRA', Extra);
    ObjectFactory.register('EXTRAPERIOD', ExtraPeriod);
    ObjectFactory.register('EXTRAPRODUCTOPT', ExtraOption);
    ObjectFactory.register('EXTRAPRODUCT', ExtraProduct);
    ObjectFactory.register('EXTRATABLE', ExtraTable);
    ObjectFactory.register('FAMILY', Family);
    ObjectFactory.register('FAMILYPERIOD', FamilyPeriod);
    ObjectFactory.register('FAMILYPRODUCT', FamilyProduct);
    ObjectFactory.register('FLOORAREA', FloorArea);
    ObjectFactory.register('I18NVALUE', I18nValue);
    ObjectFactory.register('TICKETMIXEDPAY', MixedPayment);
    ObjectFactory.register('PINPADPAY', PinpadPay);
    ObjectFactory.register('OFFER', Offer);
    ObjectFactory.register('OFFERPERIOD', OfferPeriod);
    ObjectFactory.register('OFFERPRODUCTOPT', OfferOption);
    ObjectFactory.register('OFFERPRODUCT', OfferProduct);
    ObjectFactory.register('PAYACCOUNT', PayAccount);
    ObjectFactory.register('PAYMENT', Payment);
    ObjectFactory.register('PLACE', Place);
    ObjectFactory.register('STRIPE', Stripe);
    ObjectFactory.register('PLACELINK', PlaceLink);
    ObjectFactory.register('PLACEOPT', PlaceOption);
    ObjectFactory.register('POSDEVICE', PosDevice);
    ObjectFactory.register('PRICEZONE', PriceZone);
    ObjectFactory.register('PRICETIME', PriceTime);
    ObjectFactory.register('PRODUCTPRICE', ProductPrice);
    ObjectFactory.register('PRESELECT', Preselect);
    ObjectFactory.register('PRESELECTOPT', PreselectOpt);
    ObjectFactory.register('PRINTER', PrintDevice);
    ObjectFactory.register('PRINTERPRODUCT', PrinterProduct);
    ObjectFactory.register('PRINTERPRODUCTOPT', PrinterProductOpt);
    ObjectFactory.register('PRODUCT', Product);
    ObjectFactory.register('PRODUCTOPT', ProductOpt);
    ObjectFactory.register('OPTIONPRICE', OptionPrice);
    ObjectFactory.register('QRCODE', QrCode);
    ObjectFactory.register('QRSCAN', QrScan);
    ObjectFactory.register('SWINVOICE', Swinvoice);
    ObjectFactory.register('RASPPI', Rasppi);
    ObjectFactory.register('RASPPICONNECT', RasppiConnect);
    ObjectFactory.register('SCALE', ScaleDevice);
    ObjectFactory.register('SESSION', Session);
    ObjectFactory.register('FCM', FCM);
    ObjectFactory.register('TICKET', Ticket);
    ObjectFactory.register('TILL', Till);
    ObjectFactory.register('TICKETCHANGE', TicketChange);
    ObjectFactory.register('TICKETDATA', TicketData);
    ObjectFactory.register('TICKETDISCOUNT', TicketDiscount);
    ObjectFactory.register('TICKETINVOICE', TicketInvoice);
    ObjectFactory.register('TICKETPRODUCT', TicketProduct);
    ObjectFactory.register('TICKETEXTRA', TicketExtra);
    ObjectFactory.register('TICKETOFFER', TicketOffer);
    ObjectFactory.register('TICKETOPTION', TicketOption);
    ObjectFactory.register('TICKETBAI', Ticketbai);
    ObjectFactory.register('TICKETSII', Ticketsii);
    ObjectFactory.register('VERIFACTU', Verifactu);
    ObjectFactory.register('USER', User);
}
